import { View, Text, Pressable, StyleSheet } from "react-native";
import React, { useState } from "react";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import { MotiView } from "moti";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { colors, radii, spacing, typographyScale, typographyStyles } from "../../styles/design-system";
import { formatTimeAgo } from "../../utils/date";
import { StatCard } from "../../components/adminDashboard/AICompanion";
import { AlertCircle, BookOpen, CheckSquare, ClipboardList, Megaphone, MessageSquare, UserX } from "lucide-react";
import QuickActionButton from "../../components/adminDashboard/QuickActionButton/QuickActionButton";

import CourseHealthCard from "../../components/adminDashboard/CourseHealthCard/CourseHealthCard";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { RootStackParamList } from "../../navigation/navigation.types";
import StudentListDrawer from "../../components/adminDashboard/StudentListDrawer/StudentListDrawer";
import AnnouncementModal from "../../components/adminDashboard/Announcements/AnnouncementModal";
import { AnnouncementWithStatus } from "../../graphql/generated/graphql";
import { useAppConfig } from "../../AppConfigProvider";
import { useGetFacultyDashboardQuery } from "../../graphql/generated/graphql";
import { useAuth } from "../../hooks/useAuth";
import AdminDashboardFacultySkeleton from "./AdminDashboardFacultySkeleton";

const AdminDashboardFacultyView = () => {
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshRotation, setRefreshRotation] = useState(0);
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inactiveDrawerOpen, setInactiveDrawerOpen] = useState(false);

  // Announcement
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<AnnouncementWithStatus | null>(null);
  const appConfig = useAppConfig();
  const { userInfo } = useAuth();
  const schoolId = appConfig.axioSchools[0];

  const {
    data: facultyDashboardResponse,
    loading,
    refetch,
    networkStatus,
  } = useGetFacultyDashboardQuery({
    variables: {
      schoolId,
      userId: userInfo.userId,
      searchQuery: "",
    },
    skip: !schoolId || !userInfo.userId,
    notifyOnNetworkStatusChange: true,
  });
  const activeCourse = facultyDashboardResponse
    ? facultyDashboardResponse?.getFacultyDashboard?.data?.courses?.filter((course) => !course.isDraft)
    : [];
  const isRefetching = networkStatus === 4;
  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRefreshRotation((prev) => prev + 360);

    try {
      await refetch(); // 👈 await this
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error refreshing faculty analytics:", error);
    } finally {
      setIsRefreshing(false);
    }
  };
  function getInitials(name: string): string {
    if (!name) return "?";

    const words = name.trim().split(/\s+/).filter(Boolean);

    if (words.length === 0) return "?";
    if (words.length === 1) return words[0][0]?.toUpperCase() || "?";

    const first = words[0][0] || "";
    const last = words[words.length - 1][0] || "";

    return (first + last).toUpperCase();
  }
  if (loading || isRefetching) return <AdminDashboardFacultySkeleton />;
  return (
    <View style={{ flex: 1, padding: 32 }}>
      <MotiView
        from={{ opacity: 0, translateY: 20 }}
        animate={{ opacity: 1, translateY: 0 }}
        transition={{ type: "timing", duration: 500 }}
        style={styles.headerSection}>
        <View style={styles.headerContent}>
          <View>
            <View style={styles.titleRow}>
              <Text style={styles.title}>Dashboard</Text>
            </View>
            <Text style={styles.subtitle}>Welcome to your teaching dashboard</Text>
          </View>
          <View style={styles.headerActions}>
            <View style={styles.lastUpdated}>
              <Text style={styles.lastUpdatedLabel}>Last Updated</Text>
              <Text style={styles.lastUpdatedValue}>{formatTimeAgo(lastUpdated)}</Text>
            </View>
            <Pressable
              onPress={handleRefresh}
              disabled={isRefreshing}
              style={[styles.refreshButton, isRefreshing && styles.refreshButtonDisabled]}
              aria-label="Refresh data">
              <MotiView
                animate={{ rotate: `${refreshRotation}deg` }}
                transition={{
                  rotate: {
                    type: "timing",
                    duration: 1000,
                  },
                }}>
                <ArrowsClockwiseIcon size={20} color={colors.grey[600]} weight="regular" />
              </MotiView>
            </Pressable>
          </View>
        </View>
      </MotiView>
      <MotiView
        from={{ opacity: 0, translateY: 20 }}
        animate={{ opacity: 1, translateY: 0 }}
        transition={{ type: "timing", duration: 500, delay: 100 }}
        style={styles.statsGrid}>
        <StatCard
          title="At-Risk Students"
          subtitles={`Across ${activeCourse.length} courses`}
          trend="Need Attention"
          value={
            facultyDashboardResponse
              ? facultyDashboardResponse?.getFacultyDashboard?.data?.courseWiseRosterInsights
                  ?.reduce((acc, curr) => (acc += curr?.count), 0)
                  .toString()
              : "0"
          }
          change=""
          changeType="positive"
          icon={AlertCircle}
          color="red"
          type="actionable"
          handlePress={() => setDrawerOpen((prev) => !prev)}
        />
        <StatCard
          title="Grading Queue"
          subtitles="Assignments to grade"
          trend={`${activeCourse.length} Active Courses`}
          value={
            facultyDashboardResponse
              ? facultyDashboardResponse?.getFacultyDashboard?.data?.submissions?.data?.totalCount.toString()
              : "0"
          }
          change=""
          changeType="positive"
          icon={ClipboardList}
          color="purple"
          type="actionable"
          handlePress={() => navigation.navigate("Admin Grading Center")}
        />
        <StatCard
          title="Inactive Students"
          subtitles="3+ days without login"
          trend="Check-in recommended"
          value={
            facultyDashboardResponse
              ? facultyDashboardResponse?.getFacultyDashboard?.data?.courseWiseInactiveInsights
                  ?.reduce((acc, curr) => acc + (curr?.count || 0), 0)
                  .toString()
              : "0"
          }
          change=""
          changeType="positive"
          icon={UserX}
          color="blue"
          type="actionable"
          handlePress={() => setInactiveDrawerOpen(true)}
        />
      </MotiView>
      <View style={{ marginBottom: 32 }}>
        <CustomText
          text="QUICK ACTIONS"
          size="s"
          useTranslationText={false}
          textType="text"
          weight="normal"
          role="heading"
          aria-level="1"
          style={{ color: colors.grey[600] }}
        />
        <CustomSpacing type="vertical" size="l" />
        <View style={styles.quickActionContainer}>
          <QuickActionButton
            title="Post Announcement"
            icon={Megaphone}
            onPress={() => setShowCreateModal((prev) => !prev)}
          />
          <QuickActionButton
            title="Start Discussion"
            icon={MessageSquare}
            onPress={() => navigation.navigate("Teaching")}
          />
          <QuickActionButton
            title="Grade Assignments"
            icon={CheckSquare}
            onPress={() => navigation.navigate("Admin Grading Center")}
          />
          <QuickActionButton title="View My Courses" icon={BookOpen} onPress={() => navigation.navigate("Teaching")} />
        </View>
      </View>
      <View>
        <CustomText
          text="MY COURSES"
          size="s"
          useTranslationText={false}
          textType="text"
          weight="normal"
          role="heading"
          aria-level="1"
          style={{ color: colors.grey[600] }}
        />
        <CustomSpacing type="vertical" size="l" />

        <View style={styles.courseContainer}>
          {facultyDashboardResponse &&
            facultyDashboardResponse?.getFacultyDashboard?.data?.courses?.map((course) => (
              <View style={styles.courseItem} key={course.id}>
                <CourseHealthCard
                  userId={userInfo.userId}
                  course={course}
                  atRisk={
                    facultyDashboardResponse
                      ? facultyDashboardResponse?.getFacultyDashboard?.data?.courseWiseRosterInsights.find(
                          (c) => c?.data?.courseId === course.id
                        )?.count
                      : 0
                  }
                  avgGrade={
                    facultyDashboardResponse
                      ? facultyDashboardResponse?.getFacultyDashboard?.data?.courseWiseRosterInsights.find(
                          (c) => c?.data?.courseId === course.id
                        )?.data?.cohortSummary?.avgGradePct
                      : 0
                  }
                  onPress={() =>
                    navigation.navigate("Course Information", {
                      courseId: course.id,
                      courseName: course.name,
                      returnTo: "Teaching",
                    })
                  }
                />
              </View>
            ))}
        </View>
      </View>
      <StudentListDrawer
        isOpen={drawerOpen}
        onOpenChange={setDrawerOpen}
        activeCourse={activeCourse}
        studentInsights={facultyDashboardResponse?.getFacultyDashboard?.data?.courseWiseRosterInsights ?? []}
        getInitials={getInitials}
        title="At-Risk Students"
        description={`${
          facultyDashboardResponse?.getFacultyDashboard?.data?.courseWiseRosterInsights?.flatMap(
            (i) => i?.data?.students
          ).length || 0
        } students`}
      />
      <StudentListDrawer
        isOpen={inactiveDrawerOpen}
        onOpenChange={setInactiveDrawerOpen}
        activeCourse={activeCourse}
        studentInsights={facultyDashboardResponse?.getFacultyDashboard?.data?.courseWiseInactiveInsights ?? []}
        getInitials={getInitials}
        title="Inactive Students"
        description={`${
          facultyDashboardResponse?.getFacultyDashboard?.data?.courseWiseInactiveInsights?.flatMap(
            (i) => i?.data?.students
          ).length || 0
        } students`}
      />
      <AnnouncementModal
        visible={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingAnnouncement(null);
        }}
        schoolId={schoolId}
        announcement={editingAnnouncement}
        isFaculty={true}
        onSuccess={() => {
          setShowCreateModal(false);
          setEditingAnnouncement(null);
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: colors.grey[50],
  },
  courseContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  courseItem: {
    width: "48%",
  },
  quickActionContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  actionItem: {
    width: "25%",
  },
  container: {
    maxWidth: 1280,
    alignSelf: "center",
    width: "100%",
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
    userSelect: "text",
  },
  headerSection: {
    marginBottom: spacing.xl,
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: spacing.xs,
  },
  title: {
    fontSize: typographyScale.displayMd.fontSize,
    lineHeight: typographyScale.displayMd.lineHeight,
    fontWeight: typographyStyles.fontBold.fontWeight,
    color: colors.grey[900],
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: spacing.xxs,
    backgroundColor: colors.purple[100],
    borderRadius: radii.full,
  },
  badgeText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.purple[700],
  },
  subtitle: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    color: colors.grey[600],
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  lastUpdated: {
    alignItems: "flex-end",
  },
  lastUpdatedLabel: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  lastUpdatedValue: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[700],
  },
  refreshButton: {
    width: spacing["2xl"],
    height: spacing["2xl"],
    borderRadius: 20,
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderWidth: 1,
    borderColor: colors.grey[200],
    alignItems: "center",
    justifyContent: "center",
  },
  refreshButtonDisabled: {
    opacity: 0.5,
  },
  statsGrid: {
    flexDirection: "row",
    gap: spacing.lg,
    marginBottom: spacing.xl,
  },
  tabsList: {
    marginBottom: spacing.lg,
  },
  tabContentSpacing: {
    gap: spacing.xl,
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: spacing.lg,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing.lg,
  },
  cardTitle: {
    fontSize: typographyScale.textMd.fontSize,
    lineHeight: typographyScale.textMd.lineHeight,
    fontWeight: typographyStyles.fontSemibold.fontWeight,
    color: colors.grey[900],
  },
  cardSubtitle: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    color: colors.grey[500],
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
});

export default AdminDashboardFacultyView;
